import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SavedView } from 'app/core/models';
import { map, Observable } from 'rxjs';
import { BaseService } from './base.service';
import { ReorderService } from './reorder.service';

/** Type-level views have no parent object type. Moon lists also include matching parent-scoped views. */
export const isSavedViewInScope = (
  view: SavedView,
  relatedToType: string,
  parentObjectType?: string | null,
): boolean => {
  if (view.related_to_type !== relatedToType) {
    return false;
  }

  if (!parentObjectType) {
    return !isContextualSavedView(view);
  }

  return !isContextualSavedView(view) || view.parent_object_type === parentObjectType;
};

export const isContextualSavedView = (view: SavedView): boolean => Boolean(view?.parent_object_type);

export const getParentObjectTypeViewTooltip = (view: SavedView, labelByName: Record<string, string> = {}): string => {
  if (!isContextualSavedView(view)) {
    return 'Global';
  }

  const label = labelByName[view.parent_object_type] ?? view.parent_object_type;

  return `This view belongs to ${label}`;
};

export const orderSavedViewsWithParentObjectTypeFirst = (views: SavedView[] = []): SavedView[] => {
  const list = views.filter(Boolean);

  return [...list.filter(isContextualSavedView), ...list.filter((view) => !isContextualSavedView(view))];
};

/** Type-level: drop views with a parent object type. Moon: matching parent type first, then globals. */
export const filterSavedViewsForParentObjectType = (
  views: SavedView[] = [],
  parentObjectType?: string | null,
): SavedView[] => {
  const list = views.filter(Boolean);

  if (!parentObjectType) {
    return list.filter((view) => !isContextualSavedView(view));
  }

  const scoped = list.filter((view) => view.parent_object_type === parentObjectType);
  const global = list.filter((view) => !isContextualSavedView(view));

  return [...scoped, ...global];
};

@Injectable({ providedIn: 'root' })
export default class SavedViewService extends BaseService {
  private readonly reorderService = inject(ReorderService);

  constructor(protected readonly httpClient: HttpClient) {
    super(httpClient);
  }

  get(relatedTo?: string): Observable<SavedView[]> {
    let params = new HttpParams();

    if (relatedTo) {
      params = params.set('related_to_type', relatedTo);
    }

    return this.httpClient
      .get<{ saved_views: SavedView[] }>('saved_views', { params })
      .pipe(map(({ saved_views }) => saved_views));
  }

  getById(id: number): Observable<SavedView> {
    return this.httpClient.get<any>(`saved_views/${id}`).pipe(map(({ saved_view }) => saved_view));
  }

  create(savedView: Omit<SavedView, 'id'>): Observable<SavedView> {
    return this.httpClient
      .post<any>(`saved_views`, { saved_view: savedView })
      .pipe(map(({ saved_view }) => saved_view));
  }

  update(savedView: SavedView): Observable<SavedView> {
    return this.httpClient
      .put<any>(`saved_views/${savedView.id}`, { saved_view: savedView })
      .pipe(map(({ saved_view }) => saved_view));
  }

  delete(savedViewId: number): Observable<SavedView> {
    return this.httpClient.delete<any>(`saved_views/${savedViewId}`);
  }

  reorder(idsToRanks: Record<number, number>): Observable<unknown> {
    return this.reorderService.update('SavedView', idsToRanks);
  }
}
